import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons'
import { Button, Popconfirm } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useDocumentDownload } from 'features/files/hooks/useDocumentDownload'
import { RolodexDocumentEntry } from 'features/files/types'
import { type FC, useCallback } from 'react'
import styled from 'styled-components'

const EntryActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  min-width: 5rem;
`

export interface GenericFileActionsCellProps {
  downloadEnabled: boolean
  deleteEnabled: boolean
  document: RolodexDocumentEntry
  forceRefresh?: () => Promise<void> | void
}

export const GenericFileActionsCell: FC<GenericFileActionsCellProps> = ({
  downloadEnabled,
  deleteEnabled,
  document,
  forceRefresh,
}) => {
  const { rolodexDelete } = useRolodexEdit()
  const { downloadDocument, downloadingIds } = useDocumentDownload()
  const isDownloading = downloadingIds.has(document.entry_id)

  const downloadFile = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      await downloadDocument(document)
    },
    [document, downloadDocument]
  )

  const deleteFile = useCallback(async () => {
    await rolodexDelete(document.entry_id, true)
    await forceRefresh?.()
  }, [document.entry_id, forceRefresh, rolodexDelete])

  return (
    <EntryActionCell onClick={(event) => event.stopPropagation()}>
      {downloadEnabled && (
        <Button
          aria-label='Download file'
          icon={<DownloadOutlined />}
          loading={isDownloading}
          onClick={downloadFile}
          type='text'
        />
      )}
      {deleteEnabled && (
        <Popconfirm
          title='Delete file'
          description='Are you sure you want to delete this file?'
          okText='Delete'
          okButtonProps={{ danger: true }}
          onConfirm={deleteFile}>
          <Button
            aria-label='Delete file'
            danger
            icon={<DeleteOutlined />}
            type='text'
          />
        </Popconfirm>
      )}
    </EntryActionCell>
  )
}
